import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsAppFAB from "@/components/site/WhatsAppFAB";
import { BLOG_POSTS } from "@/data/blog";
import { BRAND } from "@/data/products";
import { ArrowUpRight, Clock, Calendar } from "lucide-react";
import { useEffect } from "react";

export default function Blog() {
  useEffect(() => {
    document.title = `Journal | Vardaan Arts — Makrana Marble Temple & Craftsmanship Blog`;
  }, []);

  return (
    <div className="min-h-screen bg-cream-100 text-ink" data-testid="blog-page">
      <Header />
      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Section head */}
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-5 font-medium">
              — The Vardaan Journal
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ink leading-[1.05]">
              Stories of stone,
              <br />
              <span className="italic gold-text">craft and devotion.</span>
            </h1>
            <p className="text-ink-soft mt-6 md:mt-8 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Insights on Makrana marble, Vastu-compliant temple design,
              craftsmanship traditions and how to choose the right mandir for
              your home. Written by the family behind {BRAND.name}.
            </p>
          </div>

          {/* Featured post (first / latest) */}
          {BLOG_POSTS.length > 0 && (
            <FeaturedCard post={BLOG_POSTS[BLOG_POSTS.length - 1]} />
          )}

          {/* Rest of the posts, newest first */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-8 md:mt-10">
            {[...BLOG_POSTS.slice(0, -1)].reverse().map((post, i) => (
              <ArticleCard key={post.slug} post={post} index={i} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}

function FeaturedCard({ post }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`blog-featured-${post.slug}`}
    >
      <Link
        to={`/blog/${post.slug}`}
        className="group block bg-cream-200/40 border border-gold/25 hover:border-gold transition-colors duration-500 overflow-hidden"
      >
        <div className="grid lg:grid-cols-[1.1fr_1fr]">
          <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[520px] bg-cream-200">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
            <div className="absolute top-5 left-5 bg-cream-100 border border-gold/40 px-4 py-2">
              <div className="text-[10px] tracking-[0.28em] uppercase text-gold-dark font-medium">
                Featured Journal
              </div>
            </div>
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-5">
              <span className="flex items-center gap-2">
                <Calendar className="w-3 h-3" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-3 h-3" />
                {post.readTime}
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[42px] text-ink leading-[1.1]">
              {post.title}
            </h2>
            <p className="text-ink-soft mt-5 text-base leading-relaxed">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {post.tags.map((t) => (
                <span
                  key={t}
                  className="text-[10px] tracking-[0.22em] uppercase text-gold-dark border border-gold/40 px-3 py-1"
                >
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-2 text-[12px] tracking-[0.28em] uppercase text-ink group-hover:text-gold-dark transition-colors">
              Read the Story
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function ArticleCard({ post, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`blog-card-${post.slug}`}
    >
      <Link
        to={`/blog/${post.slug}`}
        className="group block bg-cream-100 border border-gold/25 hover:border-gold transition-colors duration-500 overflow-hidden h-full"
      >
        <div className="relative overflow-hidden aspect-[16/10] bg-cream-200">
          <img
            src={post.coverImage}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
        </div>
        <div className="p-7 md:p-8">
          <div className="flex items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-ink-soft mb-4">
            <span className="flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {formatDate(post.date)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
          </div>
          <h3 className="font-serif text-2xl md:text-[28px] text-ink leading-tight group-hover:text-gold-dark transition-colors duration-300">
            {post.title}
          </h3>
          <p className="text-ink-soft mt-3 text-[15px] leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
          <div className="flex flex-wrap gap-2 mt-5">
            {post.tags.slice(0, 2).map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.22em] uppercase text-gold-dark border border-gold/40 px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
